/** Panel route'ları için MUI tema — palette.mode context'ten, sx CSS değişkenlerinden */

import { createTheme } from "@mui/material/styles";
import { getMuiLocaleArgs } from "../../theme";
import { PANEL } from "./panelTokens";
import { getPanelMuiPalette } from "./panelCssVars";

const FONT = ["'Inter'", "sans-serif"].join(",");

const inputColorLock = {
  color: `${PANEL.text.primary} !important`,
  WebkitTextFillColor: `${PANEL.text.primary} !important`,
  caretColor: PANEL.text.primary,
};

/**
 * Doktor / tenant / super-admin panellerinde nested ThemeProvider.
 * `palette.mode` PanelThemeContext ile senkron; yüzey renkleri CSS değişkeni.
 */
export function createPanelMuiTheme(lang = "en", mode = "light") {
  const resolvedMode = mode === "dark" ? "dark" : "light";
  const pal = getPanelMuiPalette(resolvedMode);

  return createTheme(
    {
      panel: {
        isPanel: true,
        isLight: resolvedMode === "light",
      },
      palette: {
        mode: resolvedMode,
        primary: {
          main: pal.accent,
          dark: pal.accentHover,
          light: pal.accentSoft,
          contrastText: pal.accentContrast,
        },
        secondary: {
          main: pal.indigo,
          dark: pal.indigoHover,
          contrastText: pal.surface,
        },
        error: {
          main: pal.danger,
          contrastText: pal.surface,
        },
        warning: {
          main: pal.warning,
          contrastText: pal.textPrimary,
        },
        success: {
          main: pal.accentText,
          contrastText: pal.surface,
        },
        info: {
          main: pal.blue,
          contrastText: pal.surface,
        },
        text: {
          primary: pal.textPrimary,
          secondary: pal.textBody,
          disabled: pal.textMuted,
        },
        background: {
          default: pal.pageBg,
          paper: pal.surface,
        },
        divider: pal.border,
        action: {
          active: pal.textBody,
          hover: pal.fillMuted,
          selected: pal.accentSoft12,
          disabled: pal.textLabel,
          disabledBackground: pal.fillSubtle,
          focus: pal.accentFocusRing,
        },
      },
      typography: {
        fontFamily: FONT,
        htmlFontSize: 16,
        fontSize: 15,
        h1: { fontFamily: FONT, fontSize: 40, fontWeight: 700, color: PANEL.text.primary },
        h2: { fontFamily: FONT, fontSize: 32, fontWeight: 700, color: PANEL.text.primary },
        h3: { fontFamily: FONT, fontSize: 28, fontWeight: 600, color: PANEL.text.primary },
        h4: { fontFamily: FONT, fontSize: 24, fontWeight: 600, color: PANEL.text.primary },
        h5: { fontFamily: FONT, fontSize: 20, fontWeight: 600, color: PANEL.text.primary },
        h6: { fontFamily: FONT, fontSize: 18, fontWeight: 600, color: PANEL.text.primary },
        body1: { color: PANEL.text.body },
        body2: { color: PANEL.text.body },
        subtitle1: { color: PANEL.text.primary },
        subtitle2: { color: PANEL.text.body },
        caption: { color: PANEL.text.muted },
        button: {
          fontFamily: FONT,
          fontSize: 15,
          fontWeight: 600,
          textTransform: "none",
        },
      },
      shape: {
        borderRadius: 10,
      },
      components: {
        MuiCssBaseline: {
          styleOverrides: {
            body: {
              backgroundColor: PANEL.pageBg,
              backgroundImage: PANEL.pageGradient,
              backgroundRepeat: "no-repeat",
              backgroundSize: "cover",
              backgroundAttachment: "fixed",
              color: PANEL.text.primary,
            },
          },
        },
        MuiPaper: {
          styleOverrides: {
            root: {
              backgroundImage: "none",
              backgroundColor: PANEL.surface,
              color: PANEL.text.primary,
            },
          },
        },
        MuiCard: {
          defaultProps: { elevation: 0 },
          styleOverrides: {
            root: {
              backgroundColor: PANEL.surface,
              color: PANEL.text.primary,
            },
          },
        },
        MuiPopover: {
          styleOverrides: {
            paper: {
              backgroundColor: PANEL.popover,
              color: PANEL.text.primary,
              border: `1px solid ${PANEL.border}`,
            },
          },
        },
        MuiMenu: {
          styleOverrides: {
            paper: {
              backgroundColor: PANEL.popover,
              color: PANEL.text.primary,
              border: `1px solid ${PANEL.border}`,
            },
          },
        },
        MuiMenuItem: {
          styleOverrides: {
            root: {
              color: PANEL.text.body,
              "&.Mui-selected": {
                backgroundColor: PANEL.accent.soft12,
                color: PANEL.accent.text,
              },
            },
          },
        },
        MuiInputBase: {
          styleOverrides: {
            root: {
              color: PANEL.text.primary,
              backgroundColor: PANEL.surface,
            },
            input: {
              ...inputColorLock,
              "&::placeholder": {
                color: `${PANEL.text.muted} !important`,
                opacity: 1,
                WebkitTextFillColor: `${PANEL.text.muted} !important`,
              },
            },
          },
        },
        MuiOutlinedInput: {
          styleOverrides: {
            root: {
              backgroundColor: PANEL.surface,
              borderRadius: PANEL.radius.input,
              color: PANEL.text.primary,
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: PANEL.border,
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: PANEL.fill.hover,
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: PANEL.accent.focus,
              },
              "&.Mui-focused": {
                boxShadow: `0 0 0 3px ${PANEL.accent.focusRing}`,
              },
            },
            input: inputColorLock,
          },
        },
        MuiFilledInput: {
          styleOverrides: {
            root: {
              backgroundColor: PANEL.fill.muted,
              color: PANEL.text.primary,
              "&:hover": { backgroundColor: PANEL.fill.subtle },
              "&.Mui-focused": { backgroundColor: PANEL.fill.muted },
            },
            input: inputColorLock,
          },
        },
        MuiSelect: {
          styleOverrides: {
            select: inputColorLock,
            icon: { color: PANEL.text.muted },
          },
        },
        MuiInputLabel: {
          styleOverrides: {
            root: {
              color: PANEL.text.muted,
              "&.Mui-focused": { color: PANEL.accent.text },
            },
          },
        },
        MuiFormLabel: {
          styleOverrides: {
            root: {
              color: PANEL.text.muted,
              "&.Mui-focused": { color: PANEL.accent.text },
            },
          },
        },
        MuiFormHelperText: {
          styleOverrides: {
            root: { color: PANEL.text.muted },
          },
        },
        MuiToggleButton: {
          styleOverrides: {
            root: {
              color: PANEL.text.body,
              borderColor: PANEL.border,
              "&.Mui-selected": {
                color: PANEL.accent.contrast,
                backgroundColor: PANEL.accent.main,
                borderColor: PANEL.accent.main,
                "& .MuiSvgIcon-root": { color: "inherit" },
              },
            },
          },
        },
        MuiDialog: {
          defaultProps: { disableScrollLock: true },
          styleOverrides: {
            paper: {
              backgroundColor: PANEL.surface,
              color: PANEL.text.primary,
              backgroundImage: "none",
            },
          },
        },
        MuiDialogTitle: {
          styleOverrides: {
            root: { color: PANEL.text.primary },
          },
        },
        MuiDialogContent: {
          styleOverrides: {
            root: { color: PANEL.text.body },
          },
        },
        MuiButton: {
          styleOverrides: {
            root: { textTransform: "none" },
            containedPrimary: {
              backgroundColor: PANEL.accent.main,
              color: PANEL.accent.contrast,
              "&:hover": { backgroundColor: PANEL.accent.hover, color: PANEL.accent.contrast },
              "& .MuiButton-startIcon, & .MuiButton-endIcon": { color: "inherit" },
            },
            outlinedPrimary: {
              color: PANEL.text.body,
              borderColor: PANEL.border,
              "&:hover": {
                borderColor: PANEL.accent.main,
                backgroundColor: PANEL.fill.muted,
                color: PANEL.text.primary,
              },
            },
            textPrimary: {
              color: PANEL.text.body,
              "&:hover": { backgroundColor: PANEL.fill.muted, color: PANEL.text.primary },
            },
          },
        },
        MuiCheckbox: {
          styleOverrides: {
            root: { color: PANEL.text.muted },
          },
        },
        MuiRadio: {
          styleOverrides: {
            root: { color: PANEL.text.muted },
          },
        },
        MuiSwitch: {
          styleOverrides: {
            switchBase: { color: PANEL.text.muted },
          },
        },
        MuiTab: {
          styleOverrides: {
            root: {
              color: PANEL.text.muted,
              "&.Mui-selected": { color: PANEL.accent.text },
            },
          },
        },
        MuiChip: {
          styleOverrides: {
            root: { color: PANEL.text.body },
          },
        },
        MuiTableCell: {
          styleOverrides: {
            root: { color: PANEL.text.body, borderColor: PANEL.border },
            head: { color: PANEL.text.primary, fontWeight: 700 },
          },
        },
        MuiDataGrid: {
          styleOverrides: {
            root: {
              color: PANEL.text.body,
              backgroundColor: PANEL.surface,
              border: "none",
            },
            columnHeaders: {
              backgroundColor: PANEL.fill.muted,
              color: PANEL.text.primary,
            },
            cell: { color: PANEL.text.body },
            footerContainer: {
              backgroundColor: PANEL.fill.muted,
              color: PANEL.text.muted,
            },
            overlay: {
              color: PANEL.text.muted,
              backgroundColor: PANEL.surface,
            },
          },
        },
      },
    },
    ...getMuiLocaleArgs(lang)
  );
}

export function isPanelTheme(theme) {
  return theme?.panel?.isPanel === true;
}

export function isPanelLightTheme(theme) {
  return isPanelTheme(theme) && theme?.palette?.mode === "light";
}
